import React, { AriaRole } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';

const MINIMUM_DPR_FOR_SKIP_OPTIMIZATION = 2;

interface LazyImgixImageProps {
  alt?: string;
  className?: string;
  fit?: 'crop' | 'max';
  role?: AriaRole;
  skipOptimization?: boolean;
  src: string;
  sizes?: string;
  style?: React.CSSProperties;
  visibleByDefault?: boolean;
  wrapperClassName?: string;
}

const LazyImgixImage = ({
  alt,
  className,
  fit = 'crop',
  role,
  skipOptimization = false,
  src,
  sizes,
  style,
  visibleByDefault,
  wrapperClassName,
}: LazyImgixImageProps) => {
  if (!src) {
    return null;
  }

  const isGif = src.endsWith('.gif');

  const optimizationParam = !skipOptimization ? 'auto=compress&' : '';
  const formatParam = isGif ? 'gif-q=50' : 'auto=format';

  const sep = src.includes('?') ? '&' : '?';
  const imageSrc = `${src}${sep}${optimizationParam}fit=${fit}&${formatParam}`;

  const placeholderSrc = `${src}${sep}auto=compress,format&fit=crop&w=400&blur=200`;
  const dprs = [
    { dpr: 0.125, size: 50 },
    { dpr: 0.25, size: 100 },
    { dpr: 0.5, size: 200 },
    { dpr: 0.75, size: 300 },
    { dpr: 1, size: 400 },
    { dpr: 1.5, size: 600 },
    { dpr: 2, size: 800 },
    { dpr: 2.5, size: 1000 },
    { dpr: 3, size: 1200 },
    { dpr: 3.5, size: 1400 },
    { dpr: 4, size: 1600 },
    { dpr: 4.5, size: 1800 },
  ];

  const srcSet = dprs
    .map(({ dpr, size }) => {
      // If skipOptimization is enabled and the size is less than the threshold, use the minimum DPR
      const adjustedDpr = skipOptimization ? Math.max(dpr, MINIMUM_DPR_FOR_SKIP_OPTIMIZATION) : dpr;
      return `${imageSrc}&w=400&dpr=${adjustedDpr} ${size}w`;
    })
    .join(',\n');

  return (
    <LazyLoadImage
      alt={alt}
      className={className}
      loading={visibleByDefault ? 'eager' : 'lazy'}
      placeholderSrc={visibleByDefault ? undefined : placeholderSrc}
      role={role}
      sizes={sizes}
      src={imageSrc}
      srcSet={srcSet}
      style={style}
      visibleByDefault={visibleByDefault}
      wrapperClassName={wrapperClassName}
    />
  );
};

export default LazyImgixImage;
